/**
 * Hook de auditoria de exportações de dados
 * -----------------------------------------------------------------------------
 * Consome a RPC `get_export_logs`, que devolve, no mesmo retorno:
 *   - as linhas paginadas já com nome/e-mail do solicitante resolvidos;
 *   - o total de registros do recorte de filtros (`total_count`);
 *   - os indicadores do período (total, registros, envios por e-mail, falhas).
 *
 * Acesso restrito a administradores (validado no servidor).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-07 v1.1.0 - Remove kpi_rows do tipo (cartão "Registros
 *          exportados" deixou de existir; RPC segue devolvendo a coluna,
 *          apenas não é mais consumida).
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useExportLogs');

/** Linha da auditoria de exportação */
export interface ExportLogRow {
  id: string;
  created_at: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  user_avatar_url: string | null;
  export_key: string;
  format: string;
  delivery: string;
  recipient_email: string | null;
  row_count: number | null;
  status: string;
  error_message: string | null;
  filters: Record<string, unknown> | null;
  columns: unknown;
  total_count: number;
  kpi_total: number;
  kpi_email: number;
  kpi_error: number;
}

/** Filtros aceitos pela listagem */
export interface ExportLogsFilters {
  page: number;
  pageSize: number;
  dateFrom: string | null;
  dateTo: string | null;
  userId: string | null;
  exportKey: string | null;
  format: string | null;
  delivery: string | null;
  status: string | null;
  search: string | null;
}

export function useExportLogs(filtros: ExportLogsFilters) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['export-logs', currentTenant?.id, filtros],
    enabled: !!currentTenant?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<ExportLogRow[]> => {
      const { data, error } = await supabase.rpc('get_export_logs', {
        p_page: filtros.page,
        p_page_size: filtros.pageSize,
        p_date_from: filtros.dateFrom,
        p_date_to: filtros.dateTo,
        p_user_id: filtros.userId,
        p_export_key: filtros.exportKey,
        p_format: filtros.format,
        p_delivery: filtros.delivery,
        p_status: filtros.status,
        p_search: filtros.search,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);

      if (error) {
        logger.error('Erro ao carregar auditoria de exportações:', error);
        throw error;
      }
      return (data || []) as unknown as ExportLogRow[];
    },
  });
}
